import { addDays, addWeeks, endOfDay, endOfWeek, startOfDay, startOfWeek } from 'date-fns';
import { v4 } from 'uuid';

import purplePin from '../../assets/icons/MapPurplePin.svg';
import redPin from '../../assets/icons/MapRedPin.svg';
import { epochToTimeInReadableFormat } from '../../utils';

import { BULK_ACTION_TYPES, TIMEKEEPING_STATUSES, TIMEKEEPING_TAGS } from './timekeepingConstants';

export const sumDayTotalTime = (day) => {
  const value = day.reduce(
    (accumulator, curr) => accumulator + parseFloat(curr?.timecard?.totalHours ?? 0, 10),
    0
  );
  return value;
};

export const generateStatusData = (data, selectedTimeCard) => {
  const daysData = data?.days;

  const days = Object.keys(daysData);
  const returnObj = {};
  const approveList = [];
  days.forEach((day) => {
    returnObj[day] = sumDayTotalTime(daysData[day]);

    daysData[day].forEach((time) => {
      approveList.push(time?.timecard?.status);
    });
  });
  returnObj.status = {
    label:
      (approveList.includes(TIMEKEEPING_STATUSES.DISPUTED) && TIMEKEEPING_STATUSES.DISPUTED) ||
      (approveList.includes(TIMEKEEPING_STATUSES.PENDING) && TIMEKEEPING_STATUSES.PENDING) ||
      (approveList.includes(TIMEKEEPING_STATUSES.APPROVED) && TIMEKEEPING_STATUSES.APPROVED) ||
      (approveList.includes(TIMEKEEPING_STATUSES.RESOLVED) && TIMEKEEPING_STATUSES.RESOLVED) ||
      TIMEKEEPING_STATUSES.FINALIZED,
    detailViewStatus: !!selectedTimeCard,
  };

  return returnObj;
};

const getMatchingTimecards = (currentItems, newItems) => {
  const currentPlacementIds = new Set(currentItems.map((item) => item.placementId));
  const matchingItems = newItems.filter((item) => currentPlacementIds.has(item.placementId));

  return matchingItems;
};

export const getCalculatedTimecardsOnUpdate = (currentItem, newItem) => {
  const matchingTimecards =
    currentItem?.timecards && newItem?.timecards
      ? getMatchingTimecards(currentItem.timecards, newItem.timecards)
      : [];

  const totalEarnings = matchingTimecards.reduce(
    (total, item) => total + (item?.timecard?.totalEarnings || 0),
    0
  );
  const totalHours = matchingTimecards.reduce(
    (total, item) => total + (item?.timecard?.totalHours || 0),
    0
  );
  const overtimeHours = matchingTimecards.reduce(
    (total, item) => total + (item?.timecard?.overtimeHours || 0),
    0
  );
  const regularHours = matchingTimecards.reduce(
    (total, item) => total + (item?.timecard?.regularHours || 0),
    0
  );

  return {
    ...newItem,
    timecards: matchingTimecards,
    totalEarnings,
    totalHours,
    overtimeHours,
    regularHours,
  };
};

export const getTimecardsWithProcessedUpdates = (weeklyTimecards, updatedDocs) =>
  weeklyTimecards.map((weeklyCard) => {
    const updatedDocument = updatedDocs.find(
      (doc) => doc.candidate?._id === weeklyCard.candidate?._id
    );

    const placementId = updatedDocument?._id;

    if (!placementId) return weeklyCard;

    const matchingCard = weeklyCard.timecards.find((tc) => tc.placementId === placementId);

    if (matchingCard && updatedDocument?.timecard) {
      matchingCard.timecard = updatedDocument?.timecard;
    }

    return weeklyCard;
  });

export const extractPlacementIdsFromUserMap = (updatedTimecardMap) =>
  Object.keys(updatedTimecardMap)
    .map((key) => {
      // key is in the format `${candidateId}_${placementId}`
      const parts = key.split('_');
      if (parts.length === 1 || !updatedTimecardMap[key]) {
        // If there's only one part, it means it's a candidateId without a placementId
        return null;
      }
      return parts[parts.length - 1]; // Always get last part as placementId
    })
    .filter(Boolean);

// FIXME: This could be cleaner. Rather than for-eaching through this, we could filter, then sum.
export const getTotalHourCountFromSelectedTimecards = (selectedPlacements, timecards) => {
  let totalHours = 0;

  // If no placements are selected or no timecards, return 0
  if (!selectedPlacements?.length || !timecards?.length) {
    return 0;
  }

  // Convert selectedPlacements array to Set for faster lookup
  const selectedPlacementSet = new Set(selectedPlacements);

  // Iterate through each timecard entry
  timecards.forEach((timecardEntry) => {
    // Check each individual timecard in the timecards array
    timecardEntry.timecards?.forEach((timecard) => {
      // If this timecard's placementId is in our selected set, add its hours
      if (timecard.placementId && selectedPlacementSet.has(timecard.placementId)) {
        // Use totalHours from the timecard if available, otherwise sum punches
        if (timecard.timecard?.totalHours !== undefined) {
          totalHours += timecard.timecard.totalHours;
        } else if (timecard.timecard?.punches?.length) {
          // Fallback: sum up punch hours if totalHours isn't available
          const punchHours = timecard.timecard.punches.reduce(
            (sum, punch) => sum + (punch.hours || 0),
            0
          );
          totalHours += punchHours;
        }
      }
    });
  });

  // Return the total, rounded to 2 decimal places to avoid floating point precision issues
  return parseFloat(totalHours.toFixed(2));
};

export const getStartOfDayInEpoch = (date) => startOfDay(new Date(date)).getTime();
export const getEndOfDayInEpoch = (date) => endOfDay(new Date(date)).getTime();
export const getStartOfWeekInEpoch = (date) => startOfWeek(new Date(date)).getTime();
export const getEndOfWeekInEpoch = (date) => endOfWeek(new Date(date)).getTime();
export const getShiftedRangeInEpoch = ({ startEpoch, isDaily, shift }) => {
  const baseDate = new Date(startEpoch);
  const shiftedDate = isDaily ? addDays(baseDate, shift) : addWeeks(baseDate, shift);

  if (isDaily) {
    return {
      startDate: getStartOfDayInEpoch(shiftedDate),
      endDate: getEndOfDayInEpoch(shiftedDate),
    };
  }

  return {
    startDate: getStartOfWeekInEpoch(shiftedDate),
    endDate: getEndOfWeekInEpoch(shiftedDate),
  };
};

export const updateTimeCardStatus = (timecards, placements, status) =>
  timecards.map((card) => {
    let updated = {};
    Object.keys(card?.days).forEach((key) => {
      updated = {
        ...updated,
        ...{
          [key]: [
            ...card.days[key].map((item) =>
              placements.find((placement) => placement === item?.placement)
                ? { ...item, timecard: { ...item.timecard, status } }
                : item
            ),
          ],
        },
      };
    });

    return { ...card, days: updated };
  });

export const getActionButtonDisableMessage = (timecards, actonType) => {
  if (timecards.length === 0) {
    return 'timekeeping.buttonHelperText.noSelections';
  }

  // approvals need to be on pending/disputed timecards
  if (
    actonType === BULK_ACTION_TYPES.APPROVE &&
    timecards.filter(
      (x) =>
        x?.timecard?.status === TIMEKEEPING_STATUSES.PENDING ||
        x?.timecard?.status === TIMEKEEPING_STATUSES.DISPUTED
    ).length !== timecards?.length
  ) {
    return 'timekeeping.buttonHelperText.invalidApproveSelections';
  }

  // resolves need to be on pending/disputed timecards
  if (
    actonType === BULK_ACTION_TYPES.RESOLVE &&
    timecards.filter(
      (x) =>
        x?.timecard?.status === TIMEKEEPING_STATUSES.PENDING ||
        x?.timecard?.status === TIMEKEEPING_STATUSES.DISPUTED
    ).length !== timecards?.length
  ) {
    return 'timekeeping.buttonHelperText.invalidResolveSelections';
  }

  // finalize need to be on approved/resolved timecards
  if (
    actonType === BULK_ACTION_TYPES.FINALIZE &&
    timecards.filter(
      (x) =>
        x?.timecard?.status === TIMEKEEPING_STATUSES.APPROVED ||
        x?.timecard?.status === TIMEKEEPING_STATUSES.RESOLVED
    ).length !== timecards?.length
  ) {
    return 'timekeeping.buttonHelperText.invalidFinalizeSelections';
  }

  return null;
};

export const formatGpsData = ({ gpsPings, gpsPunchIn, gpsPunchOut }) => {
  const formattedGpsPings = gpsPings.map((ping) => ({
    _id: v4(),
    name: epochToTimeInReadableFormat(ping?.timestamp),
    stamp: ping.timestamp,
    lat: ping.gps.coordinates[1],
    lng: ping.gps.coordinates[0],
    radius: ping.gps.accuracy,
    isSuspicious: ping.isSuspicious,
    checked: !!ping.isSuspicious,
    icon: ping.isSuspicious ? redPin : purplePin,
  }));

  const formattedGpsPunchIn = gpsPunchIn?.gps?.coordinates
    ? {
        _id: v4(),
        name: `${epochToTimeInReadableFormat(gpsPunchIn.stamp)} (Clock In)`,
        stamp: gpsPunchIn.stamp,
        lat: gpsPunchIn?.gps?.coordinates?.latitude,
        lng: gpsPunchIn?.gps?.coordinates?.longitude,
        radius: gpsPunchIn?.gps?.coordinates?.accuracy,
        checked: false,
        icon: purplePin,
      }
    : null;

  const formattedGpsPunchOut = gpsPunchOut?.gps?.coordinates
    ? {
        _id: v4(),
        name: `${epochToTimeInReadableFormat(gpsPunchOut.stamp)} (Clock Out)`,
        stamp: gpsPunchOut.stamp,
        lat: gpsPunchOut?.gps?.coordinates?.latitude,
        lng: gpsPunchOut?.gps?.coordinates?.longitude,
        radius: gpsPunchOut?.gps?.coordinates?.accuracy,
        checked: false,
        icon: purplePin,
      }
    : null;

  return [...formattedGpsPings, formattedGpsPunchIn, formattedGpsPunchOut]
    .filter(Boolean)
    .sort((a, b) => a.stamp - b.stamp);
};

export const getCardFilterPayload = (startDate, endDate) => ({
  step1: {
    status: TIMEKEEPING_STATUSES.PENDING,
    tags: TIMEKEEPING_TAGS.LOOKS_GOOD,
    startDate,
    endDate,
  },
  step2: {
    status: TIMEKEEPING_STATUSES.PENDING,
    tags: [TIMEKEEPING_TAGS.SUSPICIOUS, TIMEKEEPING_TAGS.MISSING_PUNCH].join(','),
    startDate,
    endDate,
  },
  step3: {
    status: TIMEKEEPING_STATUSES.DISPUTED,
    startDate,
    endDate,
  },
  step4: {
    status: [TIMEKEEPING_STATUSES.APPROVED, TIMEKEEPING_STATUSES.RESOLVED].join(','),
    startDate,
    endDate,
  },
});

export const GLOBAL_FILTER_PARAM_KEYS = [
  'employee',
  'corporation',
  'agency',
  'department',
  'location',
  'position',
];

export const getGlobalFilterParams = (searchParams) =>
  GLOBAL_FILTER_PARAM_KEYS.reduce((acc, key) => {
    const value = searchParams.get(key);
    if (value) {
      acc[key] = value;
    }
    return acc;
  }, {});

export const parseGlobalUrlFilters = (searchParams) => {
  const globalFilters = [];
  const corporation = searchParams.get('corporation');
  if (corporation) {
    globalFilters.push({ value: corporation, field: 'corporation', operation: 'equalsID' });
  }
  const employee = searchParams.get('employee');
  if (employee) {
    globalFilters.push({ value: employee, field: 'candidate', operation: 'equalsID' });
  }
  const agency = searchParams.get('agency');
  if (agency) {
    globalFilters.push({
      value: agency,
      field: 'agency',
      operation: 'equalsID',
    });
  }
  const department = searchParams.get('department');
  if (department) {
    globalFilters.push({
      value: department,
      field: 'jobOrder.department._id',
      operation: 'equalsID',
    });
  }

  const location = searchParams.get('location');
  if (location) {
    globalFilters.push({ value: location, field: 'jobOrder.location', operation: 'equalsID' });
  }

  const position = searchParams.get('position');
  if (position) {
    globalFilters.push({ value: position, field: 'jobOrder.name', operation: 'icontains' });
  }

  return globalFilters;
};

export const parseSelectedUrlFilters = (searchParams) => {
  const selectedFilters = [];
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');
  if (startDate && endDate) {
    selectedFilters.push(
      { field: 'start', value: parseInt(startDate, 10), operation: 'onOrAfter' },
      { field: 'start', value: parseInt(endDate, 10), operation: 'onOrBefore' }
    );
  }

  const status = searchParams.get('status');
  if (status) {
    selectedFilters.push({
      operation: 'includedIn',
      field: 'timecard.status',
      value: status.split(','),
    });
  }

  const tags = searchParams.get('tags');
  if (tags) {
    selectedFilters.push({
      value: tags.split(','),
      field: 'timecard.tag',
      operation: 'includedIn',
    });
  }

  selectedFilters.push(...parseGlobalUrlFilters(searchParams));
  return selectedFilters;
};

export const parseOverviewUrlFilters = (searchParams) => {
  const overviewFilters = [];
  const startDate = searchParams.get('startDate');
  if (startDate) {
    overviewFilters.push({
      field: 'start',
      value: parseInt(startDate, 10),
      operation: 'onOrAfter',
    });
  }
  const endDate = searchParams.get('endDate');
  if (endDate) {
    overviewFilters.push({
      field: 'start',
      value: parseInt(endDate, 10),
      operation: 'onOrBefore',
    });
  }

  return [...overviewFilters, ...parseGlobalUrlFilters(searchParams)];
};
